import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { apiErrorText, useListIndexOptionsQuery } from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  isAnswerForCard,
  zoneDraftChanged,
  zoneFieldValue,
  type ZoneDraft,
} from './priceZoneDraft';

/**
 * Номера ценовых зон региона по утверждённым индексам (РИМ ФГИС ЦС и письма
 * Минстроя). Пусто — у региона одна зона, выбирать нечего.
 */
export function useRegionZones(region: string | null | undefined): string[] {
  const { data } = useListIndexOptionsQuery();
  if (!region) return [];
  return data?.regions.find((r) => r.region === region)?.zones ?? [];
}

/** Подпись зоны в списке: «2 зона». */
export function zoneLabel(zone: string): string {
  return /^\d+$/.test(zone) ? `${zone} зона` : zone;
}

const Box = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 600;
`;

const ZoneSelect = styled.select`
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2.5)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: inherit;
  font: inherit;
`;

const Hint = styled.div<{ $tone?: 'warning' | 'danger' }>`
  flex-basis: 100%;
  font-size: 12px;
  color: ${({ theme, $tone }) =>
    $tone === 'danger'
      ? theme.colors.status.danger
      : $tone === 'warning'
        ? theme.colors.status.warning
        : theme.colors.text.muted};
`;

interface PriceZoneFieldProps {
  /** id проверки или расчёта: черновик и ответ сохранения — только его. */
  owner: string;
  region: string | null;
  zone: string | null | undefined;
  canWrite: boolean;
  /** Сохранить зону. Ответ сервера — пояснение для сметчика. */
  onSave: (zone: string | null) => Promise<{ note: string }>;
}

/** Сообщение под полем — с карточкой, к которой оно относится. */
interface OwnedText {
  owner: string;
  text: string;
}

/**
 * Ценовая зона расчёта в карточке проверки или тендера. Список — только если у
 * региона несколько зон: тогда без выбранной зоны индексы зон не применяются.
 * После смены зоны нужен повторный расчёт — об этом говорит ответ сервера.
 * Черновик и ответ сохранения привязаны к карточке (`owner`): несохранённый
 * выбор и запоздалый ответ не переходят на другую карточку.
 */
export function PriceZoneField({ owner, region, zone, canWrite, onSave }: PriceZoneFieldProps) {
  const zones = useRegionZones(region);
  const [draft, setDraft] = useState<ZoneDraft | null>(null);
  const [note, setNote] = useState<OwnedText | null>(null);
  const [error, setError] = useState<OwnedText | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const openOwner = useRef(owner);

  useEffect(() => {
    openOwner.current = owner;
  }, [owner]);

  if (!region || zones.length <= 1) return null;

  const value = zoneFieldValue(owner, zone, draft);
  const isSaving = saving === owner;

  async function save() {
    const requestOwner = owner;
    setSaving(requestOwner);
    setNote(null);
    setError(null);
    try {
      const res = await onSave(value || null);
      if (isAnswerForCard(openOwner.current, requestOwner)) {
        setNote({ owner: requestOwner, text: res.note });
        setDraft(null);
      }
    } catch (err) {
      if (isAnswerForCard(openOwner.current, requestOwner)) {
        setError({
          owner: requestOwner,
          text: apiErrorText(err, 'Не удалось сохранить ценовую зону.'),
        });
      }
    } finally {
      setSaving((s) => (s === requestOwner ? null : s));
    }
  }

  const noteText = note?.owner === owner ? note.text : null;
  const errorText = error?.owner === owner ? error.text : null;

  return (
    <Box>
      <Label>Ценовая зона</Label>
      <ZoneSelect
        value={value}
        onChange={(e) => setDraft({ owner, value: e.target.value })}
        disabled={!canWrite || isSaving}
        aria-label="Ценовая зона"
      >
        <option value="">— не выбрана —</option>
        {zones.map((z) => (
          <option key={z} value={z}>
            {zoneLabel(z)}
          </option>
        ))}
      </ZoneSelect>
      <Button
        type="button"
        $variant="secondary"
        $size="sm"
        onClick={save}
        disabled={!canWrite || isSaving || !zoneDraftChanged(owner, zone, draft)}
        title={!canWrite ? 'Только чтение' : undefined}
      >
        {isSaving ? 'Сохраняю…' : 'Сохранить зону'}
      </Button>
      {!zone && (
        <Hint $tone="warning">
          У региона «{region}» индексы даны по ценовым зонам ({zones.join(', ')}). Пока зона не
          выбрана, индексы зон не применяются: позиции ФЕР и сборников цен 2001 остаются в
          базисе, а ГЭСН и ФСБЦ не рассчитываются.
        </Hint>
      )}
      {noteText && <Hint>{noteText}</Hint>}
      {errorText && <Hint $tone="danger">{errorText}</Hint>}
    </Box>
  );
}
